import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronDown } from 'lucide-react';
import { BalanceIcon } from './icons/BalanceIcon';
import { formatBalance } from '../../utils/balanceFormatter';
import { useWalletStore } from '../../store/walletStore';

const LOW_BALANCE_THRESHOLD = 500;

export function BalancePill() {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);

  const { wallet, fetchBalance } = useWalletStore();
  const balance = wallet?.balance ?? 0;
  const isLowBalance = balance < LOW_BALANCE_THRESHOLD;

  // Fetch balance on mount
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Click outside to dismiss
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (badgeRef.current && !badgeRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const handleRecharge = useCallback((amount: number) => {
    setIsExpanded(false);
    navigate(`/app/wallet?recharge=true&amount=${amount}`);
  }, [navigate]);

  const handleViewWallet = useCallback(() => {
    setIsExpanded(false);
    navigate('/app/wallet');
  }, [navigate]);

  // Don't render if no wallet data yet
  if (!wallet) return null;

  return (
    <div className="relative flex-shrink-0 max-w-[140px]" ref={badgeRef}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-1.5 px-2 py-1.5 rounded-full
          transition-all duration-300 hover:shadow-md active:scale-95
          ${isLowBalance
            ? 'bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-600'
            : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
          }
        `}
      >
        <div className={`
          w-6 h-6 rounded-full flex items-center justify-center
          ${isLowBalance
            ? 'bg-amber-400 dark:bg-amber-500'
            : 'bg-[#8bc43e]'
          }
        `}>
          <BalanceIcon className="w-3.5 h-3.5 text-white" />
        </div>

        <span className={`
          text-sm font-bold tabular-nums truncate
          ${isLowBalance
            ? 'text-amber-700 dark:text-amber-300'
            : 'text-gray-900 dark:text-white'
          }
        `}>
          {formatBalance(balance, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>

        <ChevronDown className={`
          w-3 h-3 text-gray-500 dark:text-gray-400 transition-transform duration-200
          ${isExpanded ? 'rotate-180' : ''}
        `} />
      </button>

      {/* Expanded Dropdown */}
      {isExpanded && (
        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          {/* Balance Info */}
          <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Wallet Balance
            </p>
            <p className={`
              text-2xl font-bold tabular-nums
              ${isLowBalance
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-gray-900 dark:text-white'
              }
            `}>
              {formatBalance(balance)}
            </p>
            {isLowBalance && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">
                Low balance - Recharge to continue charging
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="p-3 space-y-2">
            <button
              onClick={() => handleRecharge(500)}
              className="w-full bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#6fa02e] hover:to-[#8bc43e] text-white rounded-lg px-4 py-2.5 flex items-center justify-between transition-all active:scale-95"
            >
              <span className="font-semibold text-sm">Add ₹500</span>
              <Plus className="w-4 h-4" />
            </button>

            <button
              onClick={() => handleRecharge(1000)}
              className="w-full bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#6fa02e] hover:to-[#8bc43e] text-white rounded-lg px-4 py-2.5 flex items-center justify-between transition-all active:scale-95"
            >
              <span className="font-semibold text-sm">Add ₹1,000</span>
              <Plus className="w-4 h-4" />
            </button>

            <button
              onClick={handleViewWallet}
              className="w-full border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg px-4 py-2.5 flex items-center justify-center gap-2 transition-all active:scale-95 text-sm font-medium"
            >
              View Full Wallet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
